import React from 'react';
import { BarChart2 } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для быстрой настройки параметров ипотеки
 */
const QuickAdjustmentSidebar = ({ 
  formData, 
  setFormData, 
  initialPaymentPercent, 
  errors, 
  isCalculating,
  setResults,
  setSchedule,
  setErrors
}) => {
  // Функция расчета
  const calculatePaymentSchedule = () => {
    // Здесь должна быть вызвана функция основного расчета
    // В данном случае просто эмулируем действие, так как основная логика 
    // находится в родительском компоненте
    document.querySelector('button[type="submit"]')?.click();
  };

  // Обработчик изменения срока кредита
  const handleLoanTermChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      loanTerm: newValue
    }));
    
    // Очищаем ошибку если она есть
    if (errors.loanTerm) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.loanTerm;
        return newErrors;
      });
    }
  };

  // Обработчик изменения ставки
  const handleInterestRateChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      interestRate: newValue
    }));
    
    // Очищаем ошибку если она есть
    if (errors.interestRate) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.interestRate;
        return newErrors;
      });
    }
  };

  // Обработчик изменения первоначального взноса в процентах
  const handleInitialPaymentPercentChange = (e) => {
    const percent = parseInt(e.target.value, 10);
    const price = parseFloat(formData.propertyPrice) || 0;
    
    if (price > 0) {
      const newInitialPayment = Math.round(price * percent / 100).toString();
      setFormData(prev => ({
        ...prev,
        initialPayment: newInitialPayment
      }));
      
      // Очищаем ошибку если она есть
      if (errors.initialPayment) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.initialPayment;
          return newErrors;
        });
      }
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Быстрый подбор параметров</h3>
      <div className="space-y-5">
        {/* Срок кредита */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="loanTermSlider" className="text-gray-600 dark:text-gray-400">Срок кредита</label>
            <span className="font-medium text-gray-900 dark:text-white">{formData.loanTerm} лет</span>
          </div>
          <input
            type="range" id="loanTermSlider" min="1" max="50" step="1" value={formData.loanTerm}
            onChange={handleLoanTermChange} aria-label="Срок кредита (лет)"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-500 mt-1" aria-hidden="true">
            <span>1</span><span>25</span><span>50</span>
          </div>
        </div>
        
        {/* Процентная ставка */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="interestRateSlider" className="text-gray-600 dark:text-gray-400">Процентная ставка</label>
            <span className="font-medium text-gray-900 dark:text-white">{formData.interestRate}%</span>
          </div>
          <input
            type="range" id="interestRateSlider" min="0.1" max="30" step="0.1" value={formData.interestRate}
            onChange={handleInterestRateChange} aria-label="Процентная ставка (%)"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-500 mt-1" aria-hidden="true">
            <span>0.1%</span><span>15%</span><span>30%</span>
          </div>
        </div>
        
        {/* Первоначальный взнос (процент) */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="initialPaymentPercentSlider" className="text-gray-600 dark:text-gray-400">Первонач. взнос (%)</label>
            <span className="font-medium text-gray-900 dark:text-white">
              {parseFloat(formData.initialPayment).toLocaleString()} ₽ ({initialPaymentPercent.toFixed(1)}%)
            </span>
          </div>
          <input
            type="range" id="initialPaymentPercentSlider" min="0" max="95" step="1"
            value={Math.round(initialPaymentPercent)}
            onChange={handleInitialPaymentPercentChange}
            aria-label="Первоначальный взнос в процентах"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
            disabled={parseFloat(formData.propertyPrice) <= 0}
          />
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-500 mt-1" aria-hidden="true">
            <span>0%</span><span>50%</span><span>95%</span>
          </div>
        </div>
        
        {/* Кнопка пересчета */}
        <Button
          primary fullWidth onClick={calculatePaymentSchedule}
          icon={<BarChart2 className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Пересчитать
        </Button>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;